import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateWord } from '@/lib/dateFormat';
import { formatMuslinDesignValue, getMuslinDesignConfig, normalizeMuslinGarmentType } from '@/lib/muslinDesign';
import { Ruler, AlertTriangle, Package2, CheckCircle2 } from 'lucide-react';
import { useMemo, useState } from 'react';

const CONFIDENCE_TABS = ['High', 'Medium', 'Review', 'Low'];

const scoreTone = {
  High: 'bg-emerald-500/10 text-emerald-700 border-emerald-300',
  Medium: 'bg-sky-500/10 text-sky-700 border-sky-300',
  Review: 'bg-amber-500/10 text-amber-700 border-amber-300',
  Low: 'bg-slate-500/10 text-slate-700 border-slate-300',
};

const statusTone = {
  reserved: 'border-amber-200 bg-amber-50 text-amber-700',
  available: 'border-emerald-200 bg-emerald-50 text-emerald-700',
};

const formatDesignValue = (value) => {
  const normalized = formatMuslinDesignValue(value);
  return normalized || null;
};

const getDesignSpecConfig = (garmentType) => getMuslinDesignConfig(normalizeMuslinGarmentType(garmentType));

const getDesignEntries = (garmentType, profile = {}) => getDesignSpecConfig(garmentType)
  .map((item) => ({
    ...item,
    value: formatDesignValue(profile?.[item.key]),
  }))
  .filter((item) => item.value);

const getDesignComparisonRows = (garmentType, targetProfile = {}, candidateProfile = {}) => getDesignSpecConfig(garmentType)
  .map((item) => {
    const targetValue = formatDesignValue(targetProfile?.[item.key]);
    const candidateValue = formatDesignValue(candidateProfile?.[item.key]);
    if (!targetValue && !candidateValue) return null;
    const isMatch = targetValue && candidateValue && targetValue.toLowerCase() === candidateValue.toLowerCase();
    return {
      key: item.key,
      label: item.label,
      targetValue,
      candidateValue,
      isMatch,
      matchType: isMatch ? 'exact' : 'different',
    };
  })
  .filter(Boolean);

const formatMeasurementValue = (value, unit = 'in') => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return `${value}${unit}`;
};

const formatEaseValue = (value, unit = 'in') => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return `${value}${unit}`;
  }
  const sign = numeric > 0 ? '+' : '';
  return `${sign}${numeric}${unit}`;
};

const formatDeltaValue = (delta, unit = 'in') => {
  if (delta === null || delta === undefined || delta === '') {
    return 'Muslin measurement captured';
  }
  return `Muslin Δ ${delta}${unit}`;
};

const DesignBadge = ({ row }) => {
  if (!row?.candidateValue) {
    return <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[11px] text-slate-600">{row.label}: Unknown</span>;
  }
  if (!row?.targetValue) {
    return <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[11px] text-slate-700">{row.label}: {row.candidateValue}</span>;
  }
  if (row?.isMatch) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] text-emerald-700">
        <CheckCircle2 className="h-3 w-3" />
        {row.label}: {row.candidateValue}
      </span>
    );
  }
  if (row?.matchType === 'near') {
    return (
      <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] text-amber-700">
        {row.label}: {row.candidateValue} • Near match
      </span>
    );
  }
  return (
    <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] text-rose-700">
      {row.label}: {row.candidateValue}
    </span>
  );
};

export const MuslinMatchPanel = ({
  loading,
  data,
  onReuseCandidate,
  garmentType,
  targetDesignProfile = {},
}) => {
  const [confidenceFilter, setConfidenceFilter] = useState('High');
  const candidates = data?.candidates || [];
  const confidenceCounts = useMemo(() => {
    const counts = { High: 0, Medium: 0, Review: 0, Low: 0 };
    candidates.forEach((candidate) => {
      const key = CONFIDENCE_TABS.includes(candidate.confidence) ? candidate.confidence : 'Low';
      counts[key] += 1;
    });
    return counts;
  }, [candidates]);
  const visibleCandidates = candidates.filter((candidate) => {
    const key = CONFIDENCE_TABS.includes(candidate.confidence) ? candidate.confidence : 'Low';
    return key === confidenceFilter;
  });

  if (loading) {
    return (
      <div className="space-y-2" data-testid="muslin-match-loading-state">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const activeTargetDesignProfile = Object.keys(data?.target_design_profile || {}).length > 0
    ? data.target_design_profile
    : targetDesignProfile;
  const currentSelectedMuslinId = data?.current_decision?.muslin_inventory_id || null;

  return (
    <div className="flex h-full min-h-0 flex-col gap-2" data-testid="muslin-match-panel">
      {candidates.length === 0 ? (
        <Card className="border-dashed border-slate-300 bg-white/80" data-testid="muslin-match-empty-state">
          <CardContent className="py-6 text-center text-sm text-slate-600">
            <Package2 className="mx-auto mb-3 h-8 w-8 text-slate-400" />
            {data
              ? 'No reusable muslin candidates scored yet. You can still build a new muslin from the left panel.'
              : 'Press Find Close Match to load reusable muslin suggestions for this garment.'}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex shrink-0 flex-wrap gap-2 bg-slate-50/95 pb-1" data-testid="muslin-match-confidence-tabs">
            {CONFIDENCE_TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setConfidenceFilter(tab)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
                  confidenceFilter === tab
                    ? scoreTone[tab]
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}
                data-testid={`muslin-match-confidence-tab-${tab.toLowerCase()}`}
              >
                {tab} ({confidenceCounts[tab]})
              </button>
            ))}
          </div>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
          {visibleCandidates.length === 0 ? (
            <Card className="border-dashed border-slate-300 bg-white/80" data-testid="muslin-match-empty-filter-state">
              <CardContent className="py-6 text-center text-sm text-slate-600">
                No {confidenceFilter.toLowerCase()} confidence muslins in this list. Try another chip.
              </CardContent>
            </Card>
          ) : (
        <div className="grid gap-3" data-testid="muslin-match-candidate-list">
          {visibleCandidates.map((candidate) => {
            const isCurrentSelection = currentSelectedMuslinId && candidate.muslin_inventory_id === currentSelectedMuslinId;
            const designComparisonRows = (candidate.design_comparison || []).length > 0
              ? (candidate.design_comparison || []).map((row) => ({
                  ...row,
                  targetValue: formatDesignValue(row.target_value),
                  candidateValue: formatDesignValue(row.candidate_value),
                  isMatch: row.match_type === 'exact',
                  matchType: row.match_type,
                }))
              : getDesignComparisonRows(garmentType, activeTargetDesignProfile, candidate.design_profile || {});
            return (
              <Card key={candidate.muslin_inventory_id} className="border border-slate-200 bg-white shadow-sm" data-testid={`muslin-match-candidate-${candidate.muslin_inventory_id}`}>
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-base text-slate-900">{candidate.reference_code}</CardTitle>
                      <CardDescription className="mt-1 text-xs text-slate-600">
                        Box {candidate.box_code || '—'} • {candidate.store || 'No store'} • {candidate.status}
                      </CardDescription>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${statusTone[candidate.status] || 'border-slate-200 bg-slate-100 text-slate-700'}`} data-testid={`muslin-match-candidate-status-${candidate.muslin_inventory_id}`}>
                          {candidate.status === 'reserved' ? 'Reserved' : 'Available'}
                        </span>
                        {isCurrentSelection && (
                          <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-700" data-testid={`muslin-match-candidate-current-selection-${candidate.muslin_inventory_id}`}>
                            Currently selected for this garment
                          </span>
                        )}
                        {Number(candidate.active_reservation_count || 0) > 0 && (
                          <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] text-slate-700" data-testid={`muslin-match-candidate-reserved-count-${candidate.muslin_inventory_id}`}>
                            {candidate.active_reservation_count} {candidate.active_reservation_count === 1 ? 'garment using this muslin' : 'garments using this muslin'}
                          </span>
                        )}
                      </div>
                    </div>
                    <Badge className={scoreTone[candidate.confidence] || scoreTone.Low} data-testid={`muslin-match-candidate-score-${candidate.muslin_inventory_id}`}>
                      {candidate.confidence} • {candidate.score}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3" data-testid={`muslin-match-candidate-design-${candidate.muslin_inventory_id}`}>
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600">Muslin garment design</div>
                    <div className="mb-2 text-[11px] text-slate-500">Green means this muslin matches the current garment requirement.</div>
                    {designComparisonRows.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {designComparisonRows.map((row) => <DesignBadge key={`${candidate.muslin_inventory_id}-${row.key}`} row={row} />)}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500">No design profile captured for this muslin yet.</p>
                    )}
                  </div>

                  {(candidate.warnings || []).length > 0 && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid={`muslin-match-candidate-warnings-${candidate.muslin_inventory_id}`}>
                      <div className="mb-1 flex items-center gap-2 font-semibold">
                        <AlertTriangle className="h-3.5 w-3.5" /> Advisory
                      </div>
                      <ul className="space-y-1">
                        {candidate.warnings.map((warning) => <li key={warning}>• {warning}</li>)}
                      </ul>
                    </div>
                  )}

                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3" data-testid={`muslin-match-candidate-measurements-${candidate.muslin_inventory_id}`}>
                    <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                      <Ruler className="h-3.5 w-3.5" /> Body vs Muslin garment measurements
                    </div>
                    {(candidate.measurement_rows || []).length === 0 ? (
                      <p className="text-xs text-slate-500">No muslin snapshot measurements found for this muslin yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {candidate.measurement_rows.map((row) => {
                          const usedForConfidence = row.include_in_confidence !== false;
                          return (
                          <div key={`${candidate.muslin_inventory_id}-${row.field_key}`} className="grid gap-2 rounded-lg bg-white p-2 text-xs md:grid-cols-5">
                            <div className="font-semibold text-slate-800">
                              {row.label}
                              {!usedForConfidence && (
                                <div className="text-[10px] font-normal uppercase tracking-wide text-slate-400">Shown only</div>
                              )}
                            </div>
                            <div>Body {formatMeasurementValue(row.body_measurement_value, row.unit)}</div>
                            <div>Ease {formatEaseValue(row.ease_applied, row.unit)}</div>
                            <div>Expected {formatMeasurementValue(row.expected_garment_value, row.unit)}</div>
                            <div className={!usedForConfidence || row.delta === null || row.delta === undefined ? 'font-semibold text-slate-700' : Math.abs(Number(row.delta || 0)) <= 0.75 ? 'font-semibold text-emerald-700' : 'font-semibold text-amber-700'}>
                              Muslin {formatMeasurementValue(row.candidate_garment_value, row.unit)} ({formatDeltaValue(row.delta, row.unit)})
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-xs text-slate-600" data-testid={`muslin-match-candidate-reservation-${candidate.muslin_inventory_id}`}>
                      {Number(candidate.active_reservation_count || 0) > 0
                        ? `${candidate.active_reservation_count} active ${candidate.active_reservation_count === 1 ? 'reservation' : 'reservations'}`
                        : 'Not currently reserved'}
                      {candidate.status === 'reserved' && candidate.reserved_for_test_fit_date ? ` • test fit ${formatDateWord(candidate.reserved_for_test_fit_date)}` : ''}
                    </div>
                    <Button onClick={() => onReuseCandidate(candidate)} data-testid={`muslin-match-reuse-button-${candidate.muslin_inventory_id}`}>
                      Reuse & Reserve
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
          )}

      {(data?.unavailable_candidates || []).length > 0 && (
        <Card className="border border-slate-200 bg-slate-50/80" data-testid="muslin-match-unavailable-list">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Filtered out for design reasons</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-xs text-slate-700">
            {data.unavailable_candidates.map((item) => (
              <div key={item.muslin_inventory_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2">
                <span className="font-medium">{item.reference_code}</span>
                <span>{item.reason}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
          </div>
        </>
      )}
    </div>
  );
};